// src/cockpit4/components/RiskRadarChart.tsx
// ============================================
// 五维风险预警雷达（P0-功能3）- v2.0 详细改进计划
//
// 设计：企业风险预警雷达图，五维监控
//   1. 财务风险：收入下降、成本超支、现金流预警
//   2. 客户风险：客户流失、跟进不及时、投诉增加
//   3. 运营风险：任务延期、内容断更、流程卡壳
//   4. 市场风险：竞品动态、行业变化、政策风险
//   5. 人才/知识风险：知识断层、技能缺口、团队负荷
//
// 每个维度有健康度评分（0-100），低于阈值自动预警。
// 风险等级：正常(>=80) / 关注(60-79) / 警告(40-59) / 危险(<40)
// 点击维度可下钻查看详情（RiskDetailPanel）。
//
// 数据来源（按优先级）：
//   1. EnterpriseBrain.get_risks（企业总脑风险数据）
//   2. useDashboardData 的 CEO KPI + CRM 风险 + 运营数据派生
//   3. 规则引擎：基于 KPI 阈值计算各维度健康度
//
// 持久化：11_Strategy/Risk/radar-{date}.md
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  AlertTriangle,
  Activity,
  TrendingDown,
  Users,
  Brain,
  Eye,
  RefreshCw,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  ChevronRight,
  Clock,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useCockpit4Store, type CrmRiskItem } from "../store";
import RiskDetailPanel from "./RiskDetailPanel";

// ============================================
// 类型定义
// ============================================

/** 风险维度 ID */
export type RiskDimensionId = "finance" | "customer" | "operations" | "market" | "talent";

/** 风险等级 */
export type RiskLevel = "normal" | "concern" | "warning" | "critical";

/** 风险指标项 */
interface RiskIndicator {
  /** 指标名称 */
  name: string;
  /** 当前值 */
  value: number | string;
  /** 目标值/阈值 */
  target?: number | string;
  /** 单位 */
  unit?: string;
  /** 指标状态 */
  status: RiskLevel;
  /** 备注 */
  note?: string;
}

/** 风险维度 */
export interface RiskDimension {
  id: RiskDimensionId;
  /** 中文名 */
  label: string;
  /** 英文名 */
  labelEn: string;
  /** 健康度评分 0-100（100=最健康） */
  score: number;
  /** 风险等级（基于 score 计算） */
  level: RiskLevel;
  /** 维度图标 */
  icon: typeof AlertTriangle;
  /** 维度主题色 */
  color: string;
  /** 关键指标列表 */
  indicators: RiskIndicator[];
  /** 具体风险项 */
  riskItems: string[];
}

// ============================================
// 风险等级配置
// ============================================

const LEVEL_CONFIG: Record<RiskLevel, { label: string; color: string; bgColor: string }> = {
  normal: { label: "正常", color: "#10B981", bgColor: "rgba(16,185,129,0.15)" },
  concern: { label: "关注", color: "#F59E0B", bgColor: "rgba(245,158,11,0.15)" },
  warning: { label: "警告", color: "#F97316", bgColor: "rgba(249,115,22,0.15)" },
  critical: { label: "危险", color: "#EF4444", bgColor: "rgba(239,68,68,0.15)" },
};

/** 根据健康度评分计算风险等级 */
function scoreToLevel(score: number): RiskLevel {
  if (score >= 80) return "normal";
  if (score >= 60) return "concern";
  if (score >= 40) return "warning";
  return "critical";
}

// ============================================
// 风险引擎：基于 KPI + CRM + 运营数据派生五维健康度
// ============================================

interface RiskEngineContext {
  /** CEO Dashboard 数据（财务/客户/内容） */
  ceoData: any;
  /** CRM 风险客户列表 */
  crmRisks: CrmRiskItem[];
  /** 今日内容数 + 任务完成情况 */
  realKpi: { contentCount: number; taskDone: number; taskTotal: number } | null;
  /** EnterpriseBrain 风险数据 */
  brainRisks: any[];
}

/** 计算单个维度的健康度评分 */
function computeDimension(id: RiskDimensionId, ctx: RiskEngineContext): RiskDimension {
  const base: Omit<RiskDimension, "score" | "level"> = {
    id,
    label: "",
    labelEn: "",
    icon: AlertTriangle,
    color: "#6B7280",
    indicators: [],
    riskItems: [],
  };

  if (id === "finance") {
    base.label = "财务风险";
    base.labelEn = "Finance";
    base.icon = TrendingDown;
    base.color = "#10B981";
    const kpi = ctx.ceoData?.kpi;
    const revenue = kpi?.revenue ?? 0;
    const profit = kpi?.profit ?? 0;
    const cash = kpi?.cash ?? 0;
    const degraded = ctx.ceoData?.degraded === true;

    // 指标：收入（阈值 1000 元）、利润、现金流
    const revenueStatus: RiskLevel = degraded
      ? "warning"
      : revenue >= 1000
      ? "normal"
      : revenue > 0
      ? "concern"
      : "critical";
    const profitStatus: RiskLevel = profit > 0 ? "normal" : profit === 0 ? "concern" : "warning";
    const cashStatus: RiskLevel = cash > 0 ? "normal" : cash === 0 ? "warning" : "critical";

    base.indicators = [
      { name: "今日收入", value: revenue, target: 1000, unit: "元", status: revenueStatus },
      { name: "今日利润", value: profit, unit: "元", status: profitStatus },
      { name: "现金流", value: cash, unit: "元", status: cashStatus },
    ];
    if (degraded) base.riskItems.push("财务数据源未连接，KPI 使用兜底数据");
    if (revenue > 0 && revenue < 1000) base.riskItems.push(`今日收入 ${revenue} 元，低于阈值 1000 元`);
    if (revenue === 0 && !degraded) base.riskItems.push("今日无收入入账");
    if (profit < 0) base.riskItems.push(`今日亏损 ${Math.abs(profit)} 元`);

    // 健康度评分
    const score = degraded
      ? 50
      : Math.round(
          (revenue >= 1000 ? 40 : revenue > 0 ? 25 : 0) +
            (profit > 0 ? 30 : profit === 0 ? 15 : 5) +
            (cash > 0 ? 30 : cash === 0 ? 15 : 0)
        );
    return { ...base, score, level: scoreToLevel(score) };
  }

  if (id === "customer") {
    base.label = "客户风险";
    base.labelEn = "Customer";
    base.icon = Users;
    base.color = "#3B82F6";
    const kpi = ctx.ceoData?.kpi;
    const newCustomers = kpi?.newCustomers ?? 0;
    const conversionRate = kpi?.conversionRate ?? 0;
    const staleRisks = ctx.crmRisks.filter((r) => r.type === "stale");
    const closingRisks = ctx.crmRisks.filter((r) => r.type === "closing");

    const newCustStatus: RiskLevel =
      newCustomers > 0 ? "normal" : ctx.ceoData?.degraded ? "warning" : "critical";
    const conversionStatus: RiskLevel =
      conversionRate >= 30 ? "normal" : conversionRate >= 10 ? "concern" : "warning";
    const followupStatus: RiskLevel =
      staleRisks.length === 0 ? "normal" : staleRisks.length <= 3 ? "concern" : "critical";

    base.indicators = [
      { name: "今日新增客户", value: newCustomers, target: 1, unit: "人", status: newCustStatus },
      { name: "转化率", value: conversionRate, target: 30, unit: "%", status: conversionStatus },
      {
        name: "未联系客户",
        value: staleRisks.length,
        target: 0,
        unit: "个",
        status: followupStatus,
      },
    ];
    if (newCustomers === 0 && !ctx.ceoData?.degraded)
      base.riskItems.push("今日无新增客户，可能存在线索获取/转化问题");
    if (staleRisks.length > 0)
      base.riskItems.push(`${staleRisks.length} 个客户超过 30 天未联系，存在流失风险`);
    if (closingRisks.length > 0)
      base.riskItems.push(`${closingRisks.length} 个客户即将成交，需重点跟进`);

    const score = Math.round(
      (newCustomers > 0 ? 35 : 0) +
        (conversionRate >= 30 ? 30 : conversionRate >= 10 ? 20 : 10) +
        (staleRisks.length === 0 ? 35 : staleRisks.length <= 3 ? 25 : 10)
    );
    return { ...base, score, level: scoreToLevel(score) };
  }

  if (id === "operations") {
    base.label = "运营风险";
    base.labelEn = "Operations";
    base.icon = Activity;
    base.color = "#EC4899";
    const contentCount = ctx.realKpi?.contentCount ?? 0;
    const taskDone = ctx.realKpi?.taskDone ?? 0;
    const taskTotal = ctx.realKpi?.taskTotal ?? 0;
    const taskRate = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

    const contentStatus: RiskLevel =
      contentCount > 0 ? "normal" : ctx.ceoData?.degraded ? "warning" : "critical";
    const taskStatus: RiskLevel =
      taskTotal === 0 ? "concern" : taskRate >= 70 ? "normal" : taskRate >= 40 ? "warning" : "critical";

    base.indicators = [
      { name: "今日内容产出", value: contentCount, target: 1, unit: "篇", status: contentStatus },
      {
        name: "任务完成率",
        value: taskRate,
        target: 70,
        unit: "%",
        status: taskStatus,
        note: taskTotal > 0 ? `${taskDone}/${taskTotal}` : "今日无任务",
      },
    ];
    if (contentCount === 0 && !ctx.ceoData?.degraded)
      base.riskItems.push("今日无内容产出，可能存在内容断更风险");
    if (taskTotal > 0 && taskRate < 40)
      base.riskItems.push(`任务完成率仅 ${taskRate}%（${taskDone}/${taskTotal}），进度滞后`);
    if (taskTotal === 0) base.riskItems.push("今日未规划任务，建议生成日计划");

    const score = Math.round(
      (contentCount > 0 ? 50 : ctx.ceoData?.degraded ? 25 : 0) +
        (taskTotal === 0 ? 25 : taskRate >= 70 ? 50 : taskRate >= 40 ? 30 : 10)
    );
    return { ...base, score, level: scoreToLevel(score) };
  }

  if (id === "market") {
    base.label = "市场风险";
    base.labelEn = "Market";
    base.icon = Eye;
    base.color = "#F59E0B";
    // 市场风险：当前从 EnterpriseBrain 派生（如有），否则默认中性
    const brainMarketRisks = ctx.brainRisks.filter((r) =>
      String(r.title || "").match(/竞品|行业|政策|市场|market/i)
    );

    base.indicators = [
      {
        name: "竞品动态",
        value: brainMarketRisks.length > 0 ? "有变化" : "暂无监控",
        status: brainMarketRisks.length > 0 ? "concern" : "normal",
      },
      {
        name: "行业变化",
        value: "需定期评估",
        status: "concern",
        note: "建议每月评估行业趋势",
      },
      {
        name: "政策风险",
        value: "暂无",
        status: "normal",
      },
    ];
    brainMarketRisks.slice(0, 3).forEach((r) => {
      base.riskItems.push(r.title || r.impact || "市场风险项");
    });

    // 无监控数据时默认中性评分（60-70）
    const score = brainMarketRisks.length > 0 ? 55 : 70;
    return { ...base, score, level: scoreToLevel(score) };
  }

  if (id === "talent") {
    base.label = "人才/知识风险";
    base.labelEn = "Talent & Knowledge";
    base.icon = Brain;
    base.color = "#A855F7";
    const brainTalentRisks = ctx.brainRisks.filter((r) =>
      String(r.title || "").match(/知识|技能|团队|人才|负荷|knowledge|talent/i)
    );

    base.indicators = [
      {
        name: "知识断层",
        value: brainTalentRisks.length > 0 ? "存在断层" : "暂无评估",
        status: brainTalentRisks.length > 0 ? "warning" : "concern",
      },
      {
        name: "技能缺口",
        value: "需定期评估",
        status: "concern",
        note: "建议每季度评估技能矩阵",
      },
      {
        name: "团队负荷",
        value: "正常",
        status: "normal",
      },
    ];
    brainTalentRisks.slice(0, 3).forEach((r) => {
      base.riskItems.push(r.title || r.impact || "人才/知识风险项");
    });

    const score = brainTalentRisks.length > 0 ? 50 : 65;
    return { ...base, score, level: scoreToLevel(score) };
  }

  return { ...base, score: 0, level: "critical" };
}

// ============================================
// 主组件
// ============================================

export default function RiskRadarChart() {
  const dashboard = useDashboardData();
  const ceoData = dashboard.ceo;
  const crmRisks = useCockpit4Store((s) => s.crmRisks);
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  // 真实 KPI（今日内容数 + 任务完成情况）—— 从 Vault Daily 文件派生
  const [realKpi, setRealKpi] = useState<{
    contentCount: number;
    taskDone: number;
    taskTotal: number;
  } | null>(null);

  const [brainRisks, setBrainRisks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeDimension, setActiveDimension] = useState<RiskDimensionId | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // ============================================
  // 加载真实数据：今日内容数 + 任务完成 + EnterpriseBrain 风险
  // ============================================
  const loadRealData = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      // 1. 今日内容数（从 KnowledgeRuntime 资产统计）
      let contentCount = 0;
      try {
        if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.Knowledge)) {
          const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
            type: "asset_stats",
          });
          const inner = resp?.data as any;
          if (inner?.success && inner.data) {
            contentCount =
              (inner.data.todayGrowth?.cards || 0) +
              (inner.data.todayGrowth?.ideas || 0) +
              (inner.data.todayGrowth?.outputs || 0);
          }
        }
      } catch {
        // 降级
      }

      // 2. 今日任务（从 Daily 文件解析）
      let taskDone = 0;
      let taskTotal = 0;
      try {
        const today = new Date().toISOString().slice(0, 10);
        const resp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(
            `10_Operations（运营层）/Daily/${today}.md`
          )}`
        );
        if (resp.ok) {
          const json = await resp.json();
          if (json.content) {
            const doneMatches = json.content.match(/- \[x\]/gi);
            const todoMatches = json.content.match(/- \[ \]/gi);
            taskDone = doneMatches?.length || 0;
            taskTotal = taskDone + (todoMatches?.length || 0);
          }
        }
      } catch {
        // 降级
      }

      setRealKpi({ contentCount, taskDone, taskTotal });

      // 3. EnterpriseBrain 风险列表（不消耗 token，仅查询缓存）
      let risks: any[] = [];
      try {
        if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
          const resp = await RuntimeManager.query(RuntimeName.EnterpriseBrain, {
            type: "get_risks",
          });
          if (resp?.success && Array.isArray(resp.data)) risks = resp.data;
        }
      } catch {
        // 降级
      }
      setBrainRisks(risks);

      setLastUpdated(new Date().toLocaleString("zh-CN", { hour12: false }));
    } catch (err) {
      setMessage({
        type: "err",
        text: `数据加载失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // 挂载时加载 + 每 5 分钟自动刷新
  useEffect(() => {
    void loadRealData();
    const timer = setInterval(() => {
      void loadRealData();
    }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [loadRealData]);

  // ============================================
  // 计算五维风险
  // ============================================
  const dimensions: RiskDimension[] = useMemo(() => {
    const ctx: RiskEngineContext = { ceoData, crmRisks, realKpi, brainRisks };
    const ids: RiskDimensionId[] = ["finance", "customer", "operations", "market", "talent"];
    return ids.map((id) => computeDimension(id, ctx));
  }, [ceoData, crmRisks, realKpi, brainRisks]);

  // 雷达图数据格式
  const radarData = useMemo(() => {
    return dimensions.map((d) => ({
      dimension: d.label,
      score: d.score,
      fullMark: 100,
    }));
  }, [dimensions]);

  // 整体健康度（五维加权平均）
  const overallScore = useMemo(() => {
    if (dimensions.length === 0) return 0;
    return Math.round(dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length);
  }, [dimensions]);

  const overallLevel = scoreToLevel(overallScore);

  // 高风险维度（critical + warning）
  const highRiskDimensions = useMemo(
    () => dimensions.filter((d) => d.level === "critical" || d.level === "warning"),
    [dimensions]
  );

  // 全部风险项
  const totalRisks = useMemo(
    () => dimensions.reduce((sum, d) => sum + d.riskItems.length, 0),
    [dimensions]
  );

  // 持久化到 Vault
  const persistToVault = useCallback(async () => {
    const date = new Date().toISOString().slice(0, 10);
    const content = [
      `---`,
      `type: risk_radar_snapshot`,
      `date: ${date}`,
      `created_at: ${new Date().toISOString()}`,
      `overall_score: ${overallScore}`,
      `overall_level: ${overallLevel}`,
      `total_risks: ${totalRisks}`,
      `---`,
      ``,
      `# 🛡️ 五维风险预警雷达 · ${date}`,
      ``,
      `## 整体健康度：${overallScore}/100（${LEVEL_CONFIG[overallLevel].label}）`,
      ``,
      `## 五维评分`,
      ``,
      ...dimensions.map(
        (d) =>
          `- **${d.label}**：${d.score}/100（${LEVEL_CONFIG[d.level].label}）— 风险项 ${d.riskItems.length} 个`
      ),
      ``,
      `## 详细风险项`,
      ``,
      ...dimensions.flatMap((d) =>
        d.riskItems.length > 0
          ? [`### ${d.label}`, ``, ...d.riskItems.map((r) => `- ${r}`), ``]
          : []
      ),
      `---`,
      `生成时间：${lastUpdated}`,
    ].join("\n");

    try {
      await fetch(
        `/vault-api/write?path=${encodeURIComponent(`11_Strategy/Risk/radar-${date}.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );
      setMessage({
        type: "ok",
        text: `✅ 风险快照已写入 Vault：11_Strategy/Risk/radar-${date}.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ Vault 写入失败：${(err as Error).message}`,
      });
    }
  }, [dimensions, overallScore, overallLevel, totalRisks, lastUpdated]);

  // 当前激活的维度详情
  const activeDim = activeDimension
    ? dimensions.find((d) => d.id === activeDimension) || null
    : null;

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${LEVEL_CONFIG[overallLevel].color}80, transparent)`,
        }}
      />

      {/* 高风险横幅 */}
      {highRiskDimensions.length > 0 && (
        <div
          className="px-4 py-2 border-b border-white/[0.04] flex items-center gap-2"
          style={{ background: LEVEL_CONFIG[highRiskDimensions[0].level].bgColor }}
        >
          <ShieldAlert
            size={13}
            style={{ color: LEVEL_CONFIG[highRiskDimensions[0].level].color }}
          />
          <span
            className="text-[11px] font-mono uppercase tracking-wider"
            style={{ color: LEVEL_CONFIG[highRiskDimensions[0].level].color }}
          >
            预警：{highRiskDimensions.map((d) => d.label).join("、")} 维度处于风险区间
          </span>
          <span className="text-[9px] font-mono text-gray-500 ml-auto">
            共 {totalRisks} 项风险
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center"
            style={{
              background: `${LEVEL_CONFIG[overallLevel].color}15`,
              border: `1px solid ${LEVEL_CONFIG[overallLevel].color}30`,
            }}
          >
            <ShieldCheck size={13} style={{ color: LEVEL_CONFIG[overallLevel].color }} />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              五维风险预警雷达
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Risk Radar · 整体 {overallScore}/100（{LEVEL_CONFIG[overallLevel].label}）·{" "}
              {totalRisks} 项风险
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[9px] font-mono text-gray-600 flex items-center gap-1">
              <Clock size={9} />
              {lastUpdated}
            </span>
          )}
          <button
            onClick={persistToVault}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
            title="将当前风险快照写入 Vault"
          >
            <RefreshCw size={10} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
              保存快照
            </span>
          </button>
          <button
            onClick={loadRealData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 transition-colors disabled:opacity-50"
            title="刷新五维风险数据"
          >
            {loading ? (
              <Loader2 size={11} className="text-red-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-red-400" />
            )}
            <span className="text-[9px] font-mono text-red-300 uppercase tracking-wider">
              {loading ? "刷新中..." : "刷新"}
            </span>
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 主内容区：雷达图 + 五维列表 */}
      <div className="grid grid-cols-12 gap-3 p-4">
        {/* 左侧：雷达图 */}
        <div className="col-span-7 rounded-lg border border-white/[0.04] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              五维健康度雷达
            </span>
            <span
              className="px-2 py-0.5 rounded text-[9px] font-mono uppercase"
              style={{
                background: LEVEL_CONFIG[overallLevel].bgColor,
                color: LEVEL_CONFIG[overallLevel].color,
              }}
            >
              {LEVEL_CONFIG[overallLevel].label}
            </span>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="rgba(255,255,255,0.08)" />
                <PolarAngleAxis
                  dataKey="dimension"
                  tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fill: "#4B5563", fontSize: 8, fontFamily: "monospace" }}
                  stroke="rgba(255,255,255,0.05)"
                />
                <Radar
                  name="健康度"
                  dataKey="score"
                  stroke={LEVEL_CONFIG[overallLevel].color}
                  fill={LEVEL_CONFIG[overallLevel].color}
                  fillOpacity={0.35}
                  strokeWidth={1.5}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(10,14,26,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontFamily: "monospace",
                  }}
                  labelStyle={{ color: "#E5E7EB" }}
                  itemStyle={{ color: "#9CA3AF" }}
                  formatter={(value: any) => [`${value}/100`, "健康度"]}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* 等级图例 */}
          <div className="flex items-center justify-center gap-3 mt-2">
            {(Object.keys(LEVEL_CONFIG) as RiskLevel[]).map((lvl) => (
              <div key={lvl} className="flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: LEVEL_CONFIG[lvl].color }}
                />
                <span className="text-[9px] font-mono text-gray-500 uppercase">
                  {LEVEL_CONFIG[lvl].label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 右侧：五维列表（点击下钻） */}
        <div className="col-span-5 space-y-1.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              维度详情（点击下钻）
            </span>
            <span className="text-[9px] font-mono text-gray-600">
              {dimensions.filter((d) => d.level === "normal").length}/{dimensions.length} 健康
            </span>
          </div>
          {dimensions.map((d) => {
            const Icon = d.icon;
            const lvlCfg = LEVEL_CONFIG[d.level];
            return (
              <button
                key={d.id}
                onClick={() => setActiveDimension(d.id)}
                className="w-full flex items-center gap-2 p-2 rounded-md border bg-white/[0.02] hover:bg-white/[0.04] transition-all text-left"
                style={{
                  borderColor: `${lvlCfg.color}30`,
                }}
              >
                <div
                  className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center"
                  style={{ background: `${d.color}15` }}
                >
                  <Icon size={11} style={{ color: d.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-200 truncate">{d.label}</span>
                    <span
                      className="text-[10px] font-mono font-bold"
                      style={{ color: lvlCfg.color }}
                    >
                      {d.score}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {/* 健康度进度条 */}
                    <div className="flex-1 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${d.score}%`,
                          background: lvlCfg.color,
                        }}
                      />
                    </div>
                    <span
                      className="px-1 rounded text-[8px] font-mono uppercase"
                      style={{
                        background: lvlCfg.bgColor,
                        color: lvlCfg.color,
                      }}
                    >
                      {lvlCfg.label}
                    </span>
                    <span className="text-[8px] font-mono text-gray-600">
                      {d.riskItems.length}项
                    </span>
                  </div>
                </div>
                <ChevronRight size={10} className="text-gray-600 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* 下钻详情面板 */}
      <AnimatePresence>
        {activeDim && (
          <RiskDetailPanel
            dimension={activeDim}
            onClose={() => setActiveDimension(null)}
            onJumpToWorkspace={(ws) => setWorkspace(ws as any)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
